import { useState, useEffect } from 'react';
import { ArrowLeft, Check, Trophy, Medal, Crown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import BalloonMathGame from './BalloonMathGame';
import MemoryGridGame from './MemoryGridGame';
import DuelMathGame from './DuelMathGame';
import CrossMathGame from './CrossMathGame';

interface MentalMathsProps {
  onBack: () => void;
}

type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';
type GameMode = 'classic' | 'balloon' | 'memory_grid' | 'duel' | 'crossmath' | null;
type ActiveTab = 'play' | 'leaderboard';

interface Problem {
  expression: string;
  answer: number;
}

interface LeaderboardEntry {
  rank: number;
  user_id: string;
  name: string;
  country: string;
  total_score: number;
  games_played: number;
}

// Country name to code mapping
const COUNTRY_CODES: { [key: string]: string } = {
  'afghanistan': 'AF', 'albania': 'AL', 'algeria': 'DZ', 'argentina': 'AR', 'australia': 'AU',
  'austria': 'AT', 'bangladesh': 'BD', 'belgium': 'BE', 'brazil': 'BR', 'canada': 'CA',
  'chile': 'CL', 'china': 'CN', 'colombia': 'CO', 'croatia': 'HR', 'czech republic': 'CZ',
  'denmark': 'DK', 'egypt': 'EG', 'ethiopia': 'ET', 'finland': 'FI', 'france': 'FR',
  'germany': 'DE', 'ghana': 'GH', 'greece': 'GR', 'hong kong': 'HK', 'hungary': 'HU',
  'iceland': 'IS', 'india': 'IN', 'indonesia': 'ID', 'iran': 'IR', 'iraq': 'IQ',
  'ireland': 'IE', 'israel': 'IL', 'italy': 'IT', 'japan': 'JP', 'jordan': 'JO',
  'kenya': 'KE', 'south korea': 'KR', 'korea': 'KR', 'kuwait': 'KW', 'malaysia': 'MY',
  'mexico': 'MX', 'morocco': 'MA', 'nepal': 'NP', 'netherlands': 'NL', 'new zealand': 'NZ',
  'nigeria': 'NG', 'norway': 'NO', 'pakistan': 'PK', 'peru': 'PE', 'philippines': 'PH',
  'poland': 'PL', 'portugal': 'PT', 'qatar': 'QA', 'romania': 'RO', 'russia': 'RU',
  'saudi arabia': 'SA', 'singapore': 'SG', 'south africa': 'ZA', 'spain': 'ES', 'sri lanka': 'LK',
  'sweden': 'SE', 'switzerland': 'CH', 'taiwan': 'TW', 'thailand': 'TH', 'turkey': 'TR',
  'uae': 'AE', 'united arab emirates': 'AE', 'uk': 'GB', 'united kingdom': 'GB', 'great britain': 'GB',
  'usa': 'US', 'united states': 'US', 'america': 'US', 'ukraine': 'UA', 'vietnam': 'VN'
};

// Direct flag emoji mapping for common countries (fallback)
const FLAG_EMOJIS: { [key: string]: string } = {
  'IN': '🇮🇳', 'US': '🇺🇸', 'GB': '🇬🇧', 'CA': '🇨🇦', 'AU': '🇦🇺',
  'DE': '🇩🇪', 'FR': '🇫🇷', 'JP': '🇯🇵', 'CN': '🇨🇳', 'BR': '🇧🇷',
  'RU': '🇷🇺', 'KR': '🇰🇷', 'IT': '🇮🇹', 'ES': '🇪🇸', 'MX': '🇲🇽',
  'NL': '🇳🇱', 'SA': '🇸🇦', 'AE': '🇦🇪', 'SG': '🇸🇬', 'PK': '🇵🇰',
  'BD': '🇧🇩', 'NP': '🇳🇵', 'LK': '🇱🇰', 'PH': '🇵🇭', 'ID': '🇮🇩',
  'MY': '🇲🇾', 'TH': '🇹🇭', 'VN': '🇻🇳', 'ZA': '🇿🇦', 'NG': '🇳🇬',
  'EG': '🇪🇬', 'KE': '🇰🇪', 'GH': '🇬🇭', 'PL': '🇵🇱', 'UA': '🇺🇦',
  'SE': '🇸🇪', 'NO': '🇳🇴', 'DK': '🇩🇰', 'FI': '🇫🇮', 'CH': '🇨🇭',
  'AT': '🇦🇹', 'BE': '🇧🇪', 'PT': '🇵🇹', 'GR': '🇬🇷', 'TR': '🇹🇷',
  'IL': '🇮🇱', 'AR': '🇦🇷', 'CL': '🇨🇱', 'CO': '🇨🇴', 'PE': '🇵🇪',
  'NZ': '🇳🇿', 'IE': '🇮🇪', 'HK': '🇭🇰', 'TW': '🇹🇼', 'QA': '🇶🇦'
};

// Get country code from name or code
const getCountryCode = (country: string): string => {
  if (!country) return '';
  const normalized = country.toLowerCase().trim();
  // If it's already a 2-letter code, return it
  if (normalized.length === 2) return normalized.toUpperCase();
  // Look up in mapping
  return COUNTRY_CODES[normalized] || '';
};

// Country code to flag emoji mapping
const getCountryFlag = (country: string): string => {
  const countryCode = getCountryCode(country);
  if (!countryCode || countryCode.length !== 2) return '🌍';
  
  // Try direct emoji mapping first (more reliable)
  if (FLAG_EMOJIS[countryCode]) {
    return FLAG_EMOJIS[countryCode];
  }
  
  // Fallback to generating from code points
  try {
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch {
    return '🌍';
  }
};

export default function MentalMaths({ onBack }: MentalMathsProps) {
  const [gameMode, setGameMode] = useState<GameMode>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('play');
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('intermediate');
  const [duration, setDuration] = useState(3);
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [currentProblem, setCurrentProblem] = useState<Problem | null>(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState(0);
  const [streak, setStreak] = useState(0);
  const [weeklyStats, setWeeklyStats] = useState({ total_sessions: 0, total_minutes: 0 });
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [isClosingModal, setIsClosingModal] = useState(false);
  
  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [userRank, setUserRank] = useState<LeaderboardEntry | null>(null);

  useEffect(() => {
    loadStreak();
    loadWeeklyStats();
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      loadLeaderboard();
    }
  }, [activeTab]);

  useEffect(() => {
    let interval: number | undefined;
    if (isActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isActive, timeRemaining]);

  if (gameMode === 'balloon') {
    return <BalloonMathGame onBack={() => setGameMode(null)} />;
  }

  if (gameMode === 'memory_grid') {
    return <MemoryGridGame onBack={() => setGameMode(null)} />;
  }

  if (gameMode === 'duel') {
    return <DuelMathGame onBack={() => setGameMode(null)} />;
  }

  if (gameMode === 'crossmath') {
    return <CrossMathGame onBack={() => setGameMode(null)} />;
  }

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUserId(user.id);
    }
  };

  const loadLeaderboard = async () => {
    setIsLoadingLeaderboard(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Use secure database function for leaderboard
      const { data, error } = await supabase.rpc('get_mental_maths_leaderboard', {
        limit_count: 100
      });

      if (error) {
        console.error('Error loading leaderboard:', error);
        setIsLoadingLeaderboard(false);
        return;
      }

      console.log('Leaderboard data:', data); // Debug log

      if (!data || data.length === 0) {
        setLeaderboard([]);
        setIsLoadingLeaderboard(false);
        return;
      }

      // Transform data to leaderboard format
      const sortedLeaderboard: LeaderboardEntry[] = data.map((entry: any, index: number) => ({
        rank: index + 1,
        user_id: entry.user_id,
        name: entry.user_name || 'Player',
        country: entry.user_country || '',
        total_score: entry.total_score || 0,
        games_played: entry.games_played || 0
      }));

      setLeaderboard(sortedLeaderboard);

      // Find current user's rank
      if (user) {
        const userEntry = sortedLeaderboard.find(e => e.user_id === user.id);
        setUserRank(userEntry || null);
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setIsLoadingLeaderboard(false);
    }
  };

  const loadStreak = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase.rpc('get_activity_streak', {
        p_user_id: user.id,
        p_activity_type: 'mental_maths'
      });
      if (!error && data !== null) {
        setStreak(data);
      }
    } catch (error) {
      console.error('Error loading streak:', error);
    }
  };

  const loadWeeklyStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase.rpc('get_activity_weekly_stats', {
        p_user_id: user.id,
        p_activity_type: 'mental_maths'
      });
      if (!error && data && data.length > 0) {
        setWeeklyStats(data[0]);
      }
    } catch (error) {
      console.error('Error loading weekly stats:', error);
    }
  };

  const calculateExpression = (expr: string): number => {
    const tokens = expr.replace(/×/g, '*').replace(/÷/g, '/').split(' ');
    const nums: number[] = [];
    const ops: string[] = [];
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      if (!isNaN(Number(token))) {
        nums.push(Number(token));
      } else {
        ops.push(token);
      }
    }
    const multDivIndices: number[] = [];
    for (let i = 0; i < ops.length; i++) {
      if (ops[i] === '*' || ops[i] === '/') {
        multDivIndices.push(i);
      }
    }
    for (let i = multDivIndices.length - 1; i >= 0; i--) {
      const idx = multDivIndices[i];
      let val: number;
      if (ops[idx] === '*') {
        val = nums[idx] * nums[idx + 1];
      } else {
        val = nums[idx] / nums[idx + 1];
      }
      nums.splice(idx, 2, val);
      ops.splice(idx, 1);
    }
    let result = nums[0];
    for (let i = 0; i < ops.length; i++) {
      if (ops[i] === '+') {
        result += nums[i + 1];
      } else if (ops[i] === '-') {
        result -= nums[i + 1];
      }
    }
    return result;
  };

  const generateProblem = (): Problem => {
    let expression: string;
    let answer: number;
    switch (difficulty) {
      case 'beginner': {
        const num1 = Math.floor(Math.random() * 20) + 5;
        const num2 = Math.floor(Math.random() * 10) + 2;
        const num3 = Math.floor(Math.random() * 15) + 5;
        const ops = ['+', '-'];
        const op1 = ops[Math.floor(Math.random() * ops.length)];
        const op2 = ops[Math.floor(Math.random() * ops.length)];
        expression = `${num1} ${op1} ${num2} ${op2} ${num3}`;
        answer = calculateExpression(expression);
        break;
      }
      case 'intermediate': {
        const useMultDiv = Math.random() > 0.5;
        if (useMultDiv) {
          const num1 = Math.floor(Math.random() * 30) + 10;
          const num2 = Math.floor(Math.random() * 8) + 2;
          const num3 = Math.floor(Math.random() * 30) + 10;
          const op1 = '×';
          const ops = ['+', '-'];
          const op2 = ops[Math.floor(Math.random() * ops.length)];
          expression = `${num1} ${op1} ${num2} ${op2} ${num3}`;
          answer = Math.round(calculateExpression(expression));
        } else {
          const num1 = Math.floor(Math.random() * 50) + 20;
          const num2 = Math.floor(Math.random() * 20) + 5;
          const num3 = Math.floor(Math.random() * 30) + 10;
          const ops = ['+', '-'];
          const op1 = ops[Math.floor(Math.random() * ops.length)];
          const op2 = ops[Math.floor(Math.random() * ops.length)];
          expression = `${num1} ${op1} ${num2} ${op2} ${num3}`;
          answer = calculateExpression(expression);
        }
        break;
      }
      case 'advanced': {
        const patterns = [
          () => {
            const num1 = Math.floor(Math.random() * 50) + 20;
            const num2 = Math.floor(Math.random() * 8) + 2;
            const num3 = Math.floor(Math.random() * 20) + 10;
            const num4 = Math.floor(Math.random() * 5) + 2;
            return `${num1} × ${num2} + ${num3} - ${num4}`;
          },
          () => {
            const num1 = Math.floor(Math.random() * 80) + 40;
            const num2 = Math.floor(Math.random() * 8) + 2;
            const num3 = Math.floor(Math.random() * 6) + 2;
            return `${num1} - ${num2} × ${num3}`;
          },
          () => {
            const num1 = Math.floor(Math.random() * 30) + 10;
            const num2 = Math.floor(Math.random() * 8) + 2;
            const num3 = Math.floor(Math.random() * 40) + 20;
            const num4 = Math.floor(Math.random() * 6) + 2;
            return `${num1} × ${num2} - ${num3} + ${num4}`;
          },
          () => {
            const divisor = Math.floor(Math.random() * 8) + 2;
            const multiplier = Math.floor(Math.random() * 12) + 5;
            const num1 = divisor * multiplier;
            const num2 = Math.floor(Math.random() * 30) + 10;
            return `${num1} ÷ ${divisor} + ${num2}`;
          }
        ];
        const pattern = patterns[Math.floor(Math.random() * patterns.length)];
        expression = pattern();
        answer = Math.round(calculateExpression(expression));
        break;
      }
      default:
        expression = '10 + 5';
        answer = 15;
    }
    return { expression, answer };
  };

  const handleStart = () => {
    setGameMode('classic');
    setIsActive(true);
    setTimeRemaining(duration * 60);
    setScore(0);
    setTotalAttempts(0);
    setWrongAnswers(0);
    setCurrentProblem(generateProblem());
    setUserAnswer('');
  };

  const handleNumberClick = (num: string) => {
    setUserAnswer(prev => prev + num);
  };

  const handleBackspace = () => {
    setUserAnswer(prev => prev.slice(0, -1));
  };

  const handleSubmitAnswer = () => {
    if (!currentProblem || !userAnswer) return;
    const userNum = parseFloat(userAnswer);
    const isCorrect = Math.abs(userNum - currentProblem.answer) < 0.1;
    setTotalAttempts(prev => prev + 1);
    if (isCorrect) {
      setScore(prev => prev + 1);
    } else {
      setWrongAnswers(prev => prev + 1);
    }
    setCurrentProblem(generateProblem());
    setUserAnswer('');
  };

  const handleComplete = async () => {
    setIsActive(false);
    setShowResultsModal(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const accuracyVal = totalAttempts > 0 ? (score / totalAttempts) * 100 : 0;
      await supabase.from('mind_body_activities').insert({
        user_id: user.id,
        activity_type: 'mental_maths',
        duration_minutes: duration,
        difficulty_level: difficulty,
        score: score,
        accuracy: accuracyVal,
        completed_at: new Date().toISOString()
      });
      await loadStreak();
      await loadWeeklyStats();
    } catch (error) {
      console.error('Error saving mental maths session:', error);
    }
  };

  const handleCloseModal = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowResultsModal(false);
      setIsClosingModal(false);
      setGameMode(null);
      onBack();
    }, 300);
  };

  const handlePlayAgain = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowResultsModal(false);
      setIsClosingModal(false);
      handleStart();
    }, 300);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const accuracy = totalAttempts > 0 ? Math.round((score / totalAttempts) * 100) : 0;

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown size={18} className="text-yellow-500" />;
      case 2:
        return <Medal size={18} className="text-gray-400" />;
      case 3:
        return <Medal size={18} className="text-amber-600" />;
      default:
        return <span className="text-sm font-bold text-gray-500 w-[18px] text-center">{rank}</span>;
    }
  };

  const getRankBg = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
      case 3:
        return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
      default:
        return 'bg-white border-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="max-w-lg mx-auto">
        <header className="bg-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 border-b border-gray-200">
          <button
            onClick={gameMode === 'classic' && isActive ? () => { setIsActive(false); setGameMode(null); } : onBack}
            className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-700" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xl">🧠</span>
            <h1 className="text-base font-bold text-gray-900">Mental Maths</h1>
          </div>
          <div className="w-9"></div>
        </header>

        <div className="p-4 space-y-4">
          {!isActive ? (
            <>
              {/* Tab Switcher */}
              <div className="bg-white rounded-2xl p-1.5 border border-gray-200 flex gap-1">
                <button
                  onClick={() => setActiveTab('play')}
                  className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    activeTab === 'play'
                      ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/20'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span>🎮</span> Play
                </button>
                <button
                  onClick={() => setActiveTab('leaderboard')}
                  className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    activeTab === 'leaderboard'
                      ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/20'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Trophy size={16} /> Leaderboard
                </button>
              </div>

              {activeTab === 'play' ? (
                <>
                  {/* Stats Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-2xl p-4 border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base">🔥</span>
                        <p className="text-xs font-medium text-gray-500">Streak</p>
                      </div>
                      <p className="text-2xl font-black text-gray-900">{streak} <span className="text-sm font-medium text-gray-400">days</span></p>
                    </div>
                    <div className="bg-white rounded-2xl p-4 border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base">📊</span>
                        <p className="text-xs font-medium text-gray-500">This Week</p>
                      </div>
                      <p className="text-2xl font-black text-gray-900">{weeklyStats.total_sessions} <span className="text-sm font-medium text-gray-400">sessions</span></p>
                    </div>
                  </div>

                  {/* Main Card */}
                  <div className="bg-white rounded-2xl p-5 border border-gray-200">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mb-3 shadow-lg shadow-cyan-500/20">
                        <span className="text-3xl">🧮</span>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Train Your Brain</h2>
                      <p className="text-sm text-gray-500 mt-1">Choose your training mode</p>
                    </div>

                    <div className="mb-4">
                      <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Game Mode</p>
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <button className="p-3 rounded-xl border-2 border-cyan-500 bg-cyan-50 text-center transition-all active:scale-[0.98]">
                          <span className="text-2xl mb-1 block">🧮</span>
                          <p className="font-bold text-xs text-cyan-700">Classic</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">Solve math</p>
                        </button>
                        <button
                          onClick={() => setGameMode('balloon')}
                          className="p-3 rounded-xl border-2 border-gray-200 hover:border-pink-500 hover:bg-pink-50 text-center transition-all active:scale-[0.98] group"
                        >
                          <span className="text-2xl mb-1 block">🎈</span>
                          <p className="font-bold text-xs text-gray-700 group-hover:text-pink-700">Balloon</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">Pop balloons</p>
                        </button>
                        <button
                          onClick={() => setGameMode('memory_grid')}
                          className="p-3 rounded-xl border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 text-center transition-all active:scale-[0.98] group"
                        >
                          <span className="text-2xl mb-1 block">🧠</span>
                          <p className="font-bold text-xs text-gray-700 group-hover:text-purple-700">Memory</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">Grid pattern</p>
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setGameMode('duel')}
                          className="p-3 rounded-xl border-2 border-gray-200 hover:border-orange-500 hover:bg-orange-50 text-center transition-all active:scale-[0.98] group"
                        >
                          <span className="text-2xl mb-1 block">⚔️</span>
                          <p className="font-bold text-xs text-gray-700 group-hover:text-orange-700">Duel</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">Live PvP</p>
                        </button>
                        <button
                          onClick={() => setGameMode('crossmath')}
                          className="p-3 rounded-xl border-2 border-gray-200 hover:border-indigo-500 hover:bg-indigo-50 text-center transition-all active:scale-[0.98] group"
                        >
                          <span className="text-2xl mb-1 block">🧩</span>
                          <p className="font-bold text-xs text-gray-700 group-hover:text-indigo-700">Cross Math</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">Math puzzle</p>
                        </button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Duration</p>
                      <div className="grid grid-cols-4 gap-2">
                        {[1, 3, 5, 10].map((min) => (
                          <button
                            key={min}
                            onClick={() => setDuration(min)}
                            className={`py-2.5 rounded-xl font-bold text-sm transition-all active:scale-[0.95] ${
                              duration === min
                                ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/20'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {min}m
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mb-5">
                      <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Difficulty</p>
                      <div className="space-y-2">
                        {([
                          { level: 'beginner' as DifficultyLevel, emoji: '🌱', label: 'Beginner', desc: 'Addition & Subtraction' },
                          { level: 'intermediate' as DifficultyLevel, emoji: '⭐', label: 'Intermediate', desc: 'Mixed Operations' },
                          { level: 'advanced' as DifficultyLevel, emoji: '🚀', label: 'Advanced', desc: 'Complex Expressions' }
                        ]).map((item) => (
                          <button
                            key={item.level}
                            onClick={() => setDifficulty(item.level)}
                            className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all active:scale-[0.99] border-2 ${
                              difficulty === item.level
                                ? 'border-cyan-500 bg-cyan-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <span className="text-xl">{item.emoji}</span>
                            <div className="text-left flex-1">
                              <p className={`font-semibold text-sm ${difficulty === item.level ? 'text-cyan-700' : 'text-gray-900'}`}>
                                {item.label}
                              </p>
                              <p className="text-xs text-gray-500">{item.desc}</p>
                            </div>
                            {difficulty === item.level && (
                              <div className="w-5 h-5 bg-cyan-600 rounded-full flex items-center justify-center">
                                <Check size={12} className="text-white" strokeWidth={3} />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={handleStart}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-xl font-bold text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                      <span className="text-lg">▶️</span>
                      Start Challenge
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Leaderboard Tab */}
                  {/* Current User Rank Card */}
                  {userRank && (
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                            <span className="text-2xl font-black">#{userRank.rank}</span>
                          </div>
                          <div>
                            <p className="font-bold">Your Ranking</p>
                            <p className="text-sm text-cyan-100">{userRank.games_played} games played</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-black">{userRank.total_score.toLocaleString()}</p>
                          <p className="text-xs text-cyan-100">Total Score</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Leaderboard List */}
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Trophy size={18} className="text-yellow-500" />
                        <h3 className="font-bold text-gray-900">Top Players</h3>
                      </div>
                      <button 
                        onClick={loadLeaderboard}
                        className="text-xs text-cyan-600 font-medium hover:underline"
                      >
                        Refresh
                      </button>
                    </div>

                    {isLoadingLeaderboard ? (
                      <div className="p-8 text-center">
                        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-sm text-gray-500">Loading leaderboard...</p>
                      </div>
                    ) : leaderboard.length === 0 ? (
                      <div className="p-8 text-center">
                        <span className="text-4xl mb-2 block">🏆</span>
                        <p className="text-gray-500 text-sm">No scores yet. Be the first!</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
                        {leaderboard.map((entry) => (
                          <div
                            key={entry.user_id}
                            className={`px-4 py-3 flex items-center gap-3 ${getRankBg(entry.rank)} ${
                              entry.user_id === currentUserId ? 'ring-2 ring-cyan-500 ring-inset' : ''
                            }`}
                          >
                            <div className="w-8 flex justify-center">
                              {getRankIcon(entry.rank)}
                            </div>
                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-xl">
                              {getCountryFlag(entry.country)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 truncate text-sm">
                                {entry.name}
                                {entry.user_id === currentUserId && (
                                  <span className="ml-2 text-xs text-cyan-600 font-normal">(You)</span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500">{entry.games_played} games</p>
                            </div>
                            <div className="text-right">
                              <p className="font-black text-gray-900">{entry.total_score.toLocaleString()}</p>
                              <p className="text-[10px] text-gray-400">points</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {/* Active Game - Same as before */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">⏱️</span>
                    <p className="text-xs font-medium text-gray-500">Time Left</p>
                  </div>
                  <p className="text-2xl font-black text-gray-900">{formatTime(timeRemaining)}</p>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">🎯</span>
                    <p className="text-xs font-medium text-gray-500">Score</p>
                  </div>
                  <p className="text-2xl font-black text-gray-900">{score}</p>
                  <p className="text-[10px] text-gray-500">{accuracy}% accuracy</p>
                </div>
              </div>

              {currentProblem && (
                <div className="bg-white rounded-2xl p-5 border border-gray-200">
                  <div className="text-center mb-4">
                    <div className="bg-gray-50 rounded-2xl py-6 px-4 mb-4 border border-gray-100">
                      <p className="text-3xl font-black text-gray-900 tracking-tight">
                        {currentProblem.expression}
                      </p>
                    </div>
                    <p className="text-gray-400 text-sm mb-2">= ?</p>
                    <div className="bg-white rounded-xl p-4 mb-4 min-h-[56px] flex items-center justify-center border-2 border-gray-200">
                      <p className="text-3xl font-black text-gray-900">
                        {userAnswer || '—'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                      <button
                        key={num}
                        onClick={() => handleNumberClick(num.toString())}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-900 text-xl font-bold py-4 rounded-xl border border-gray-200 transition-all active:scale-95"
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <button
                      onClick={() => handleNumberClick('-')}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-900 text-xl font-bold py-4 rounded-xl border border-gray-200 transition-all active:scale-95"
                    >
                      −
                    </button>
                    <button
                      onClick={() => handleNumberClick('0')}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-900 text-xl font-bold py-4 rounded-xl border border-gray-200 transition-all active:scale-95"
                    >
                      0
                    </button>
                    <button
                      onClick={() => handleNumberClick('.')}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-900 text-xl font-bold py-4 rounded-xl border border-gray-200 transition-all active:scale-95"
                    >
                      .
                    </button>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    <button
                      onClick={handleBackspace}
                      className="col-span-2 bg-red-50 hover:bg-red-100 text-red-600 py-3.5 rounded-xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-1 border border-red-100"
                    >
                      ⌫ Delete
                    </button>
                    <button
                      onClick={handleSubmitAnswer}
                      disabled={!userAnswer}
                      className="col-span-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-3.5 rounded-xl font-bold text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/20"
                    >
                      Submit →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showResultsModal && (
        <div 
          className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ${
            isClosingModal ? 'bg-black/0' : 'bg-black/50'
          }`}
          onClick={handleCloseModal}
        >
          <div 
            className={`w-full max-w-lg bg-white rounded-t-[28px] transform transition-transform duration-300 ease-out ${
              isClosingModal ? 'translate-y-full' : 'translate-y-0'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <div className="px-5 pb-6">
              <div className="text-center mb-4">
                <div className="text-6xl mb-2">🏆</div>
                <h2 className="text-xl font-bold text-gray-900">Time's Up!</h2>
                <p className="text-sm text-gray-500">Great effort! Here's your results</p>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl">✅</span>
                  </div>
                  <p className="text-2xl font-black text-green-600">{score}</p>
                  <p className="text-xs text-green-600 font-medium">Correct</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-center">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl">❌</span>
                  </div>
                  <p className="text-2xl font-black text-red-600">{wrongAnswers}</p>
                  <p className="text-xs text-red-600 font-medium">Wrong</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500">Accuracy</span>
                  <span className="text-sm font-bold text-gray-900">{accuracy}%</span>
                </div>
                <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-all duration-500"
                    style={{ width: `${accuracy}%` }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleCloseModal}
                  className="py-3.5 rounded-xl font-bold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all active:scale-[0.98]"
                >
                  Done
                </button>
                <button
                  onClick={handlePlayAgain}
                  className="py-3.5 rounded-xl font-bold text-sm bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20 transition-all active:scale-[0.98]"
                >
                  Play Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}